import React, { useEffect, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckCircleIcon from "@mui/icons-material/CheckCircleOutlined";
import ErrorIcon from "@mui/icons-material/ErrorOutlined";
import { useNavigate, useParams } from "react-router-dom";
import Loader from "../../components/Molecules/Loader/Loader";
import useFetchComparisonScoringConfigById from "../../hooks/useFetchComparisonScoringConfigById";
import { useBreadCrumbContext } from "../../contexts/BreadCrumbContext";
import { useAuth } from "../../contexts/AuthContext";
import { getRuleDetail } from "../../services/rulesService";
import { IconBtnEditBase } from "../../common/utils/icons";

const GRID_COLUMN_SPACING = { xl: 4, lg: 3, md: 2, sm: 2, xs: 1 };

const ViewComparisonScoring: React.FC = () => {
  const { id: configId } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canEdit = hasPermission("comparison_scoring_config", "edit");
  const { setCurrentBreadcrumbPage } = useBreadCrumbContext();
  const [isLoading, setIsLoading] = useState(false);
  const [ruleName, setRuleName] = useState<string | null>(null);

  const [configData] = useFetchComparisonScoringConfigById({
    setIsLoading,
    configId: configId ? parseInt(configId) : undefined,
  });

  useEffect(() => {
    if (configData?.rule_id) {
      getRuleDetail(configData.rule_id)
        .then((rule: any) => {
          if (rule?.name) setRuleName(rule.name);
        })
        .catch(() => {});
    }
  }, [configData?.rule_id]);

  useEffect(() => {
    setCurrentBreadcrumbPage({
      name: `Config #${configId}`,
      id: configId ? parseInt(configId) : null,
    });
    return () => setCurrentBreadcrumbPage({ name: "", id: null });
  }, [configId]);

  const field = (label: string, value: any, md: number = 4) => (
    <Grid item xs={12} sm={6} md={md}>
      <label className="label-text">{label}</label>
      <div className="form-control">{value ?? "—"}</div>
    </Grid>
  );

  const factorField = (
    label: string,
    value: number | undefined,
    md: number = 4,
  ) => (
    <Grid item xs={12} md={md}>
      <label className="label-text">{label}</label>
      <TextField
        type="number"
        value={value ?? ""}
        disabled
        InputProps={{ readOnly: true }}
        className="form-control"
      />
    </Grid>
  );

  const weightsSum =
    (configData?.category_weight_missing ?? 0) +
    (configData?.category_weight_mismatch ?? 0) +
    (configData?.category_weight_validation ?? 0);
  const weightsSumValid = Math.abs(weightsSum - 1.0) < 0.001;

  const severityEntries: [string, string][] = Object.entries(
    (configData?.severity_mappings || {}) as Record<string, string>,
  );
  const hasSeverityMappings = severityEntries.length > 0;
  const hasCustomSql = Boolean(configData?.custom_factor_sql);

  return (
    <Box>
      {isLoading && <Loader isLoading={isLoading} />}
      <Box className="text-box-card-white">
        <Box className="text-box-header">
          <h3>View Comparison Scoring Config</h3>
        </Box>

        {/* Rule + Edit button */}
        <Grid container rowSpacing={2.5} columnSpacing={GRID_COLUMN_SPACING}>
          {field(
            "Rule",
            ruleName
              ? `${ruleName} (${configData?.rule_id})`
              : configData?.rule_id,
          )}
          {canEdit && (
            <Grid
              item
              xs={12}
              sm={6}
              md
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
              }}
            >
              <button
                className="btn-nostyle icon-btn-edit"
                onClick={() => navigate(`/scoring/comparison/edit/${configId}`)}
              >
                <Box className="d-block">
                  <IconBtnEditBase ariaLabel="Edit Comparison Scoring Config" />
                </Box>
              </button>
            </Grid>
          )}
        </Grid>

        {/* Category Weights */}
        <Typography variant="body2" sx={{ mt: "20px", mb: 1, fontWeight: 600 }}>
          Category Weights
        </Typography>
        <Grid container rowSpacing={2.5} columnSpacing={GRID_COLUMN_SPACING}>
          {factorField(
            "Missing Weight",
            configData?.category_weight_missing,
            3,
          )}
          {factorField(
            "Mismatched Weight",
            configData?.category_weight_mismatch,
            3,
          )}
          {factorField(
            "Validation Weight",
            configData?.category_weight_validation,
            3,
          )}
          <Grid
            item
            xs={12}
            md={3}
            sx={{ display: "flex", alignItems: "center" }}
          >
            {weightsSumValid ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  color: "success.main",
                }}
              >
                <CheckCircleIcon fontSize="small" sx={{ mr: 0.5 }} />
                <Typography variant="body2">
                  Sum: {weightsSum.toFixed(2)}
                </Typography>
              </Box>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  color: "error.main",
                }}
              >
                <ErrorIcon fontSize="small" sx={{ mr: 0.5 }} />
                <Typography variant="body2">
                  Sum: {weightsSum.toFixed(2)} (must be 1.0)
                </Typography>
              </Box>
            )}
          </Grid>
        </Grid>

        {/* Missing Records */}
        <Typography variant="body2" sx={{ mt: "20px", mb: 1, fontWeight: 600 }}>
          Missing Records
        </Typography>
        <Grid container rowSpacing={2.5} columnSpacing={GRID_COLUMN_SPACING}>
          {factorField("Blend Ratio", configData?.blend_ratio, 4)}
          {factorField("Missing Factor", configData?.missing_factor, 4)}
        </Grid>

        {/* Mismatched Records */}
        <Typography variant="body2" sx={{ mt: "20px", mb: 1, fontWeight: 600 }}>
          Mismatched Records
        </Typography>
        <Grid container rowSpacing={2.5} columnSpacing={GRID_COLUMN_SPACING}>
          {factorField("Factor (Low)", configData?.mismatch_factor_low, 4)}
          {factorField(
            "Factor (Medium)",
            configData?.mismatch_factor_medium,
            4,
          )}
          {factorField("Factor (High)", configData?.mismatch_factor_high, 4)}
        </Grid>

        {/* Severity Mappings */}
        {hasSeverityMappings && (
          <>
            <Typography
              variant="body2"
              sx={{ mt: "20px", mb: 1, fontWeight: 600 }}
            >
              Severity Mappings (check_name to severity)
            </Typography>
            <Box
              sx={{
                border: "1px solid var(--table-border)",
                borderRadius: "4px",
                overflow: "hidden",
              }}
            >
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <strong>Check Name</strong>
                      </TableCell>
                      <TableCell sx={{ width: 160 }}>
                        <strong>Severity</strong>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {severityEntries.map(([checkName, severity]) => (
                      <TableRow key={checkName}>
                        <TableCell>{checkName}</TableCell>
                        <TableCell sx={{ width: 160 }}>{severity}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </>
        )}

        {/* Custom SQL Override */}
        {hasCustomSql && (
          <Accordion
            sx={{
              mt: "20px",
              border: "1px solid var(--table-border)",
              boxShadow: "none",
              "&:before": { display: "none" },
              borderRadius: "4px !important",
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Custom Factor SQL Override (Advanced)
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid
                container
                rowSpacing={2.5}
                columnSpacing={GRID_COLUMN_SPACING}
              >
                <Grid item xs={12} md={4}>
                  <label className="label-text">Apply Factor To</label>
                  <div className="form-control">
                    {configData?.custom_factor_target ?? "—"}
                  </div>
                </Grid>
                <Grid item xs={12}>
                  <label className="label-text">
                    Custom Factor SQL{" "}
                    <span style={{ fontWeight: 400, opacity: 0.7 }}>
                      (must return a single float value)
                    </span>
                  </label>
                  <TextField
                    fullWidth
                    value={configData?.custom_factor_sql ?? ""}
                    disabled
                    InputProps={{ readOnly: true }}
                    className="form-control"
                    multiline
                    rows={4}
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        )}
      </Box>
    </Box>
  );
};

export default ViewComparisonScoring;
